// src/pages/lead/LeadAnnouncements.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLeadData } from "../../context/LeadContext";
import {
  formatDate,
  postAnnouncement,
} from "../../hooks/useFirestore";
import { db } from "../../firebase/config";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import {
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  addDoc,
} from "firebase/firestore";
import { useNotifications } from "../../context/NotificationContext";

const priorityIcon = { high: "🔴", medium: "🟡", low: "🟢" };
const emptyForm = { title: "", message: "", priority: "medium" };

export default function LeadAnnouncements() {
  const { userProfile } = useAuth();
  const { announcements, announcementsLoading: loading, members: users } = useLeadData();
  const { addNotification } = useNotifications();

  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [deleting, setDeleting] = useState({});

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg]   = useState("");

  const [localAnnouncements, setLocalAnnouncements] = useState([]);

  useEffect(() => {
    setLocalAnnouncements(announcements);
  }, [announcements]);

  

  async function handlePost(e) {
    e.preventDefault();
    setSaving(true);
    setMsg("");

    const duplicateAnnouncement = announcements.find(
  (a) =>
    a.title?.trim().toLowerCase() ===
      form.title.trim().toLowerCase() &&
    a.message?.trim().toLowerCase() ===
      form.message.trim().toLowerCase()
);

if (duplicateAnnouncement) {
  setMsg("❌ Duplicate announcement already exists");
  setSaving(false);
  return;
}

const onlySpecialChars = /^[^a-zA-Z0-9]+$/;

if (
  onlySpecialChars.test(form.title.trim()) ||
  onlySpecialChars.test(form.message.trim())
) {
  setMsg("❌ Title and Message cannot contain only special characters");
  setSaving(false);
  return;
}

if (!form.title.trim() || !form.message.trim()) {
  setMsg("❌ Title and Message cannot be empty or contain only spaces");
  setSaving(false);
  return;
}


    try {
      const newAnn = await postAnnouncement({
        title: form.title,
        message: form.message,
        priority: form.priority,
        audience: userProfile.uid,
        postedBy: userProfile.uid,
        postedByName: userProfile.displayName,
        teamId: userProfile.uid,
        teamName: userProfile.department || "General",
      });

      await addNotification({
          type: "info",
          title: "Team Announcement",
          message: `${userProfile.displayName} posted: ${form.title}`,
          recipientId: userProfile.uid,
          link: "/employee/dashboard"
      });

      setMsg("✅ Posted successfully!");
      setForm(emptyForm);
      setLocalAnnouncements((prev) => [newAnn, ...prev]);
    } catch (err) {
      setMsg("❌ " + err.message);
    }
    setSaving(false);
  }

  function openEdit(a) {
    setEditItem(a);
    setEditForm({ title: a.title, message: a.message, priority: a.priority });
    setEditMsg("");
  }

  async function handleEditSave() {
    setEditSaving(true);
    try {
      await updateDoc(doc(db, "announcements", editItem.id), {
        ...editForm,
        updatedAt: serverTimestamp(),
      });
      setLocalAnnouncements((prev) =>
        prev.map((a) => (a.id === editItem.id ? { ...a, ...editForm } : a))
      );
      setEditMsg("✅ Updated!");
      setTimeout(() => {
        setEditItem(null);
        setEditMsg("");
      }, 900);
    } catch (err) {
      setEditMsg("❌ " + err.message);
    }
    setEditSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this announcement?")) return;
    setDeleting((d) => ({ ...d, [id]: true }));
    try {
      await deleteDoc(doc(db, "announcements", id));
      setLocalAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error(err);
    }
    setDeleting((d) => ({ ...d, [id]: false }));
  }

  function getReadStatus(a) {
    const readSet = new Set(a.readBy || []);
    const relevant = users.filter((u) => u.role !== "admin");
    return {
      read: relevant.filter((u) => readSet.has(u.uid)),
      unread: relevant.filter((u) => !readSet.has(u.uid)),
    };
  }

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <div className="page-header">
        <h1 className="page-title">Announcements</h1>
      </div>

      <div className="two-col-layout">
        <div className="form-card">
          <h3 className="form-card-title">Post Team Announcement</h3>
          {msg && (
            <div className={msg.startsWith("✅") ? "form-success" : "form-error"}>
              {msg}
            </div>
          )}
          <form onSubmit={handlePost}>
            <div className="form-group">
              <label>Title *</label>
              <input
                value={form.title}
                maxLength={1000}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                required
                placeholder="Announcement title"
              />
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea
                value={form.message}
                maxLength={1000}
                placeholder="Announcement message"
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                required
                rows={4}
              />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              >
                <option value="high">🔴 High</option>
                <option value="medium">🟡 Medium</option>
                <option value="low">🟢 Low</option>
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Posting…" : "Post Announcement"}
            </button>
          </form>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">All Announcements</h3>
          </div>
          {loading ? (
            <div className="card-loading">Loading…</div>
          ) : localAnnouncements.length === 0 ? (
            <div className="card-empty">No announcements.</div>
          ) : (
            <div className="announcements-full-list">
              {localAnnouncements.map((a) => {
                const { read, unread } = getReadStatus(a);
                const isExpanded = expandedId === a.id;
                const isOwnPost = a.postedBy === userProfile?.uid;
                return (
                  <div key={a.id} className={`announcement-card priority-${a.priority}`}>

  <div className="announcement-card-header">
    <span>
      {priorityIcon[a.priority]} {a.title}
    </span>

    <div
      style={{
        display: "flex",
        gap: "0.4rem",
        alignItems: "center",
      }}
    >
      {isOwnPost && (
        <>
          <button
            className="ann-icon-btn"
            onClick={() => openEdit(a)}
            title="Edit"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
              />
            </svg>
          </button>

          <button
            className="ann-icon-btn ann-icon-btn--danger"
            onClick={() => handleDelete(a.id)}
            disabled={deleting[a.id]}
            title="Delete"
          >
            {deleting[a.id] ? (
              "…"
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                width="16"
                height="16"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                />
              </svg>
            )}
          </button>
        </>
      )}
    </div>
  </div>

  <p className="announcement-card-msg">
    {a.message}
  </p>


  <div className="announcement-card-meta">
    <span>By {a.postedByName} {a.teamName ? `(${a.teamName})` : ""}</span>
    <span>{formatDate(a.createdAt)}</span>
  </div>

  <button
    className="ann-read-toggle"
    onClick={() => setExpandedId(isExpanded ? null : a.id)}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="14"
        height="14"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
        />
      </svg>

      {read.length} seen
    </span>

    ·

    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="14"
        height="14"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
        />
      </svg>

      {unread.length} unseen
    </span>

    <span style={{ marginLeft: "0.3rem" }}>
      {isExpanded ? "▲" : "▼"}
    </span>
  </button>

  {isExpanded && (
    <div className="ann-read-panel">

      <div className="ann-read-section">

        <div className="ann-read-section-title">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="14"
            height="14"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>

          Seen ({read.length})
        </div>

        {read.length === 0 ? (
          <div className="ann-read-empty">
            No one has seen this yet.
          </div>
        ) : (
          read.map((u) => (
            <div
              key={u.uid}
              className="ann-read-row ann-read-row--seen"
            >
              <span className="ann-read-avatar">
                {u.displayName?.slice(0, 2).toUpperCase()}
              </span>

              <span className="ann-read-name">
                {u.displayName}
              </span>

              <span className="ann-eye-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  width="14"
                  height="14"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </span>
            </div>
          ))
        )}
      </div>

      <div
        className="ann-read-section"
        style={{ marginTop: "0.75rem" }}
      >
        <div className="ann-read-section-title">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            width="14"
            height="14"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
            />
          </svg>

          Not seen ({unread.length})
        </div>

        {unread.length === 0 ? (
          <div className="ann-read-empty">
            Everyone has seen this.
          </div>
        ) : (
          unread.map((u) => (
            <div
              key={u.uid}
              className="ann-read-row ann-read-row--unseen"
            >
              <span className="ann-read-avatar">
                {u.displayName?.slice(0, 2).toUpperCase()}
              </span>

              <span className="ann-read-name">
                {u.displayName}
              </span>

              <span className="ann-eye-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  width="14"
                  height="14"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              </span>
            </div>
          ))
        )}
      </div>

    </div>
  )}

</div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editItem && (
        <div className="modal-overlay" onClick={() => setEditItem(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Announcement</h2>
              <button className="modal-close" onClick={() => setEditItem(null)}>✕</button>
            </div>
            {editMsg && <div className={editMsg.startsWith("✅") ? "form-success" : "form-error"}>{editMsg}</div>}
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Message *</label>
              <textarea className="form-input" rows={4} value={editForm.message} onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-select" value={editForm.priority} onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}>
                <option value="high">🔴 High</option>
                <option value="medium">🟡 Medium</option>
                <option value="low">🟢 Low</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleEditSave} disabled={editSaving}>{editSaving ? "Saving…" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
